import { computed, type ComputedRef } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

import type { TooltipBindingValue } from "@/directives/tooltipDirective";
import type { TooltipPlacement } from "@/stores/useTooltip";

import { useProjectRevisionsStore } from "../stores/projectRevisions.store";

/**
 * The one place that answers "may this surface write project data right now?" while a revision is
 * being previewed, and the one place that words the refusal.
 *
 * Why a preview has to be read-only: reads pin to the previewed revision, but writes cannot — a
 * material write goes to the API/Marten head and a Hasura insert is trigger-stamped onto the
 * CURRENT revision's rows. So a save made while previewing edits today's project while the screen
 * shows an older save point: no visible change, no feedback, and data changed that the user was not
 * looking at. Blocking the write is the only correct outcome (RTLCA-5253).
 *
 * Deliberately `isPreviewing` and not the broader `readPinRevisionIdFor` — the same choice
 * `EditProject.vue` documents. Previewing is one client's explicit, reversible look at a frozen
 * revision. Being *switched* to a revision is different: that revision's rows ARE the working
 * state, so the whole team must keep editing normally.
 */

/** One line, so it fits the `label` tooltip variant. The preview banner carries the full story. */
export const REVISION_READ_ONLY_HINT = "Read-only — switch to the current revision to make changes";

/** Said out loud when a write is actually attempted (a drop, or a click on a blocked control). */
export const REVISION_READ_ONLY_MESSAGE =
  "You are previewing an older revision. Use “Back to current” to make changes — they are only ever saved to the current revision.";

/**
 * The provider/store form of `blockedByPreview`, for a guard that lives outside a route-bound
 * component — a Pinia store method or a composable handed its projectId per call
 * (`TransportMappingListProvider`, `useSpeckleEpdAssign`). Same condition, same refusal, resolved
 * at call time so a captured id or a stale flag can never answer for the wrong project.
 */
export const blockedByPreviewFor = (projectId: string): boolean => {
  if (!useProjectRevisionsStore().isPreviewing(projectId)) return false;
  useToast().info(REVISION_READ_ONLY_MESSAGE);
  return true;
};

export interface RevisionReadOnly {
  /** True while this client is previewing a frozen revision of the project in the current route. */
  isReadOnly: ComputedRef<boolean>;
  /** Ready-made `v-tooltip` binding that explains the block, and suppresses itself when editing is allowed. */
  readOnlyTooltip: ComputedRef<TooltipBindingValue>;
  /**
   * The second guard layer. Call it FIRST in every handler that mutates project data and return
   * early when it is true: hiding or disabling a control cannot cover a modal that was already open
   * when the revision changed, a keyboard path, or a drop event mid-flight.
   */
  blockedByPreview: () => boolean;
  /**
   * `blockedByPreview` for handlers that are nothing but the guard: runs `action` unless the
   * preview blocks it (then the refusal toast is spoken instead). Made for the template —
   * `@click="runUnlessBlocked(toggle)"` on a popover trigger keeps the menu shut while previewing
   * without every surface writing the same three-line wrapper.
   */
  runUnlessBlocked: (action: () => unknown) => void;
  /**
   * Click-capture for a blocked control that sits inside a larger click target (the
   * include-mapping checkboxes live on rows that expand or open a modal). While previewing it
   * swallows the click — the row behind must not react as if something happened — and speaks the
   * refusal; on the current revision it does nothing, so the click keeps bubbling untouched.
   */
  stopBlockedClick: (event: MouseEvent) => void;
}

/**
 * @param placement where the explanatory tooltip should sit relative to the blocked control.
 */
export const useRevisionReadOnly = (placement: TooltipPlacement = "bottom"): RevisionReadOnly => {
  const route = useRoute();
  const revisionsStore = useProjectRevisionsStore();

  // Every caller lives under `/project/:id`, so the route param is the project the store is keyed
  // by — including the modals, which only open from those routes. Resolved reactively rather than
  // captured, so switching project without unmounting still asks about the right project.
  const projectId = computed(() => String(route.params.id ?? ""));

  const isReadOnly = computed(() => revisionsStore.isPreviewing(projectId.value));

  const readOnlyTooltip = computed<TooltipBindingValue>(() => ({
    text: REVISION_READ_ONLY_HINT,
    variant: "label",
    placement,
    // Suppressed rather than unbound, so the same binding can stay on a control that is only
    // sometimes blocked without ever explaining a control the user can actually use.
    disabled: !isReadOnly.value,
  }));

  const blockedByPreview = (): boolean => blockedByPreviewFor(projectId.value);

  const runUnlessBlocked = (action: () => unknown): void => {
    if (blockedByPreview()) return;
    action();
  };

  const stopBlockedClick = (event: MouseEvent): void => {
    if (!isReadOnly.value) return;
    // Stopped only while blocked — on the current revision the click must keep bubbling to
    // whatever the surrounding row does with it (expand, open a modal).
    event.stopPropagation();
    blockedByPreview();
  };

  return { isReadOnly, readOnlyTooltip, blockedByPreview, runUnlessBlocked, stopBlockedClick };
};

export default useRevisionReadOnly;
